<template>
  <div>
    <apollo-error-alert
      v-if="bookError"
      :error="bookError"
    />
    <div
      v-else
      class="d-flex"
    >
      <div class="mr-3">
        <book-cover-image
          class="book-cover-image"
          :src="book && book.thumbnailUrl ? book.thumbnailUrl : ''"
          :alt="$t('bookInfo.bookCoverAlt', { title: orderLine.name })"
        >
          <template #placeholder>
            <image-loading />
          </template>
        </book-cover-image>
      </div>
      <div class="d-flex flex-column justify-space-between mr-3">
        <h3 class="subtitle-2 font-weight-bold">
          {{ decodeHtml(orderLine.name) }}
        </h3>
        <div v-if="book && book.authors && book.authors.length > 0">
          <h3 class="subtitle-2 font-weight-regular">
            <span>
              {{ $t("bookInfo.by") }}
            </span>
            <span
              v-for="(author, index) in authorList"
              :key="index"
              class="mr-1"
            >
              {{
                index + 1 == authorList.length
                  ? decodeHtml(author)
                  : $t("bookInfo.authorListItem", {
                    author: decodeHtml(author),
                  })
              }}
            </span>
            <span
              v-if="remainingAuthorsCount > 0"
              class="book-info__author"
            >
              {{ $t("bookInfo.moreAuthors", { count: remainingAuthorsCount }) }}
            </span>
          </h3>
        </div>
        <h4
          v-else
          class="book-info__author"
        >
          {{ $t("bookInfo.authorUnknown") }}
        </h4>
        <div class="mt-auto">
          <div
            v-if="showPrice && orderCurrency"
            class="d-flex"
          >
            <transition
              v-if="!hasDiscount"
              name="price-change"
              mode="out-in"
            >
              <div
                :key="formattedPrice"
                class="gradient-text text-left subtitle-2 font-weight-bold mt-auto"
              >
                {{ formattedPrice }}
              </div>
            </transition>
            <transition
              v-if="hasDiscount"
              name="price-change"
              mode="out-in"
            >
              <div
                :key="formattedPriceBeforeDiscount"
                class="gradient-text text-left subtitle-2 font-weight-bold mt-auto mr-2 striked-text"
              >
                {{ formattedPriceBeforeDiscount }}
              </div>
            </transition>
            <transition
              v-if="hasDiscount"
              name="price-change"
              mode="out-in"
            >
              <div
                :key="formattedPrice"
                class="gradient-text text-left subtitle-2 font-weight-bold mt-auto discount-text"
              >
                {{ formattedPrice }}
              </div>
            </transition>
          </div>
          <h5
            v-if="hasDiscount"
            class="discount-text"
          >
            {{ $t("checkout.hasCoupon") }}
          </h5>
        </div>
      </div>
    </div>
  </div>
</template>

<script>
import { decodeHtml } from "@/helpers/generalFunctions";
import BookCoverImage from "@/components/BookCoverImage";
import gql from "graphql-tag";
import ApolloErrorAlert from "@/components/ApolloErrorAlert.vue";
import { FORMATS } from "@/utils/Const";
import currencyMixins from "@/mixins/currencyMixins";
import {
  getOrderLineFinalPrice,
  getOrderLineOriginalPrice,
} from "@/utils/priceCalculator";

export default {
  name: "OrderBookDisplayItem",
  components: {
    BookCoverImage,
    ApolloErrorAlert,
  },
  mixins: [currencyMixins],
  props: {
    order: {
      type: Object,
      default: null,
    },
    orderLine: {
      type: Object,
      required: true,
    },
    showPrice: {
      type: Boolean,
      default: true,
    },
    showQuantityInput: {
      type: Boolean,
      default: false,
    },
  },
  data() {
    const cached = this.$store.getters.cachedBook(this.orderLine.productId);
    return {
      book: cached || {},
      authorsShownCount: 2,
      bookError: null,
      formattedPrice: '',
      formattedPriceBeforeDiscount: '',
    };
  },
  computed: {
    orderCurrency() {
      return this.order?.currency;
    },
    authorList() {
      return this.book?.authors ? this.book.authors.slice(0, this.authorsShownCount) : [];
    },
    remainingAuthorsCount() {
      return this.book?.authors ? this.book.authors.length - this.authorList.length : 0;
    },
    hasDiscount() {
      return this.orderLine.discount && this.orderLine.discount > 0;
    },
    formats() {
      return FORMATS;
    },
    selectedCurrencyCode() {
      return this.getCurrencyCode();
    },
  },
  watch: {
    selectedCurrencyCode: 'updateFormattedPrices',
    orderCurrency: { handler: 'updateFormattedPrices', deep: true },
    'orderLine.price': 'updateFormattedPrices',
    'orderLine.discount': 'updateFormattedPrices',
    'orderLine.basePrice': 'updateFormattedPrices',
    'order.taxRate.rate': 'updateFormattedPrices',
  },
  created() {
    this._priceUpdateSeq = 0;
    this.updateFormattedPrices();
  },
  apollo: {
    book: {
      query: gql`
        query Book($id: ID!) {
          book(id: $id) {
            id
            thumbnailUrl
            authors
          }
        }
      `,
      fetchPolicy: "cache-first",
      variables() {
        return {
          id: "/books/" + this.orderLine.productId,
        };
      },
      skip() {
        return !!this.$store.getters.cachedBook(this.orderLine.productId);
      },
      result({ data }) {
        if (data?.book) {
          this.$store.commit("cacheBook", {
            productId: this.orderLine.productId,
            data: { thumbnailUrl: data.book.thumbnailUrl, authors: data.book.authors },
          });
        }
      },
      error(error) {
        this.bookError = error;
      },
    },
  },
  methods: {
    decodeHtml,
    async updateFormattedPrices() {
      if (!this.orderCurrency) return;
      const seq = ++this._priceUpdateSeq;
      const baseCurrency = this.orderCurrency._id;
      const selectedCurrency = this.selectedCurrencyCode;

      const currentPrice = getOrderLineFinalPrice(this.orderLine, this.order);
      const price = await this.getPrice(baseCurrency, currentPrice, 0, true, 1);
      if (seq !== this._priceUpdateSeq) return;
      const newFormattedPrice = this.getPriceString(selectedCurrency, price, true);
      if (newFormattedPrice !== this.formattedPrice) {
        this.formattedPrice = newFormattedPrice;
      }

      if (this.hasDiscount) {
        const originalPrice = getOrderLineOriginalPrice(this.orderLine, this.order);
        const convertedOriginal = await this.getPrice(baseCurrency, originalPrice, 0, true, 1);
        if (seq !== this._priceUpdateSeq) return;
        const newBeforeDiscount = this.getPriceString(selectedCurrency, convertedOriginal, true);
        if (newBeforeDiscount !== this.formattedPriceBeforeDiscount) {
          this.formattedPriceBeforeDiscount = newBeforeDiscount;
        }
      } else if (this.formattedPriceBeforeDiscount !== '') {
        this.formattedPriceBeforeDiscount = '';
      }
    },
  },
};
</script>

<style scoped>
.book-cover-image {
  width: 62px;
  height: 90px;
}

.discount-text,
.discount-text.gradient-text {
  color: var(--v-success-base);
  -webkit-text-fill-color: currentColor;
}
</style>
